import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import useLzFApiCall from "@lz/services/Api";
import { useLzOnMount } from "@lz/services/Hooks";
import { lzFShowSuccessMessage, useLzFUrlQueryParams } from "@lz/services/Utils";
import { LzTFormSizes, LzTOpenObject } from "@lz/typings/types";
import LzMCrud from "@lz/models/crud/Crud";
import LzMBase, { LzTBase } from "@lz/models/commons/Base";
import LzXForm from "./Form";

export interface LzPCrudEdit {
    crud: LzMCrud,
    modelRef: LzTBase,
    formSize?: LzTFormSizes,
    onRecordLoaded?: (record: LzMBase) => void,
    onRecordChanged?: (record: LzMBase) => void,
    onRecordSaved?: (record: LzMBase) => void,
    children?: React.ReactNode;
}

const LzXCrudEdit = (props: LzPCrudEdit) => {
    if (typeof props.crud === 'undefined' || !props.crud || '' === props.crud.ident) {
        return <Alert variant="filled" severity="error">CRUD configurations is missing!</Alert>;
    }

    if (typeof props.modelRef === 'undefined' || !props.modelRef) {
        return <Alert variant="filled" severity="error">Model definition is missing!</Alert>;
    }

    const [isLoadingModel, setIsLoadingModel] = useState(true);
    const [loadingModelErrMsg, setloadingModelErrMsg] = useState('');
    const [model, setModel] = useState<LzMBase | null>(null);

    const routeParams = useParams();
    const lzApiCall = useLzFApiCall();
    const navigate = useNavigate();
    const location = useLocation();
    const lzFUrlQueryParams = useLzFUrlQueryParams();
    const locState: LzTOpenObject = location.state as LzTOpenObject;

    const loadRecord = async (id: number) => {
        const data = await lzApiCall(props.crud.ident + '/' + id);

        if (data) {
            const record = LzMBase.buildModel(data, props.modelRef);
            if (props.onRecordLoaded) {
                props.onRecordLoaded(record);
            }
            return record;
        }

        return null;
    };

    const reloadRecord = async () => {
        const id = +(routeParams.id || 0);

        if (id < 1) {
            setloadingModelErrMsg('Record ID is not provided!');
            setIsLoadingModel(false);
            return;
        }

        const record = await loadRecord(id);
        if (!record) {
            setloadingModelErrMsg('Failed to load record!');
            setIsLoadingModel(false);
            return;
        }

        setIsLoadingModel(false);
        setModel(record);
    };

    useLzOnMount(() => {
        reloadRecord();
    });


    if (!model) {
        if (!isLoadingModel) {
            return <Alert variant="filled" severity="error">{'' === loadingModelErrMsg ? 'Record cannot be loaded!' : loadingModelErrMsg}</Alert>;
        }

        return <CircularProgress />
    }

    const handleOnSubmit = async (record: LzMBase): Promise<boolean> => {
        const data = await lzApiCall(props.crud.ident + '/' + record.id, record, 'put');
        if (data) {
            lzFShowSuccessMessage('Record updated successfully!');
            if (props.onRecordSaved) {
                props.onRecordSaved(data);
            }
            return true;
        }
        return false;
    };

    const handleOnRecordChange = (record: LzMBase | null) => {
        if (props.onRecordChanged && record) {
            props.onRecordChanged(record);
        }
    };

    const handleOnCancel = () => navigate(props.crud.getRouteOnEditClose(model, locState, lzFUrlQueryParams()), { state: locState });

    return (
        <React.Fragment>

            <LzXForm
                fields={props.crud.fields}
                inputs={props.crud.editFormInputs}
                modelRef={props.modelRef}
                model={model}
                formSize={props.formSize}
                isTreeBased={props.crud.isTreeBased}
                onSubmit={handleOnSubmit}
                onCancel={handleOnCancel}
                onReload={reloadRecord}
                onChange={handleOnRecordChange}
                className={props.crud.className}
            />

        </React.Fragment>
    );

};

export default LzXCrudEdit;
